import axios, { AxiosResponse } from 'axios'
import { ID } from '../../../../../../_metronic/helpers'
import {
  FormularioCampoView,
  FormularioDtoCampoCreate,
  FormularioDtoCampoUpdate,
  FormularioDtoCreate,
  FormularioDtoOpcaoCreate,
  FormularioDtoOpcaoUpdate,
  FormularioDtoUpdate,
  FormularioView,
} from './_models'

const API_URL = process.env.REACT_APP_API_URL
const FORMULARIOS_URL = `${API_URL}/formularios`
const CAMPOS_URL = `${API_URL}/formularios/campo`
const OPCOES_URL = `${API_URL}/formularios/opcao`

const FORMULARIOS_EMPRESA_URL = `${API_URL}/formularios/empresa`
const CAMPOS_EMPRESA_URL = `${API_URL}/formularios/empresa/campo`
const OPCOES_EMPRESA_URL = `${API_URL}/formularios/empresa/opcao`

export const getFormularioById = (id: ID): Promise<FormularioView> => {
  return axios
    .get(`${FORMULARIOS_URL}/${id}`)
    .then((resp: AxiosResponse<FormularioView>) => resp.data)
}

export const createFormulario = (formulario: FormularioDtoCreate): Promise<FormularioView> => {
  return axios
    .post(`${FORMULARIOS_URL}`, formulario)
    .then((resp: AxiosResponse<FormularioView>) => resp.data)
}

export const updateFormulario = (formulario: FormularioDtoUpdate): Promise<any> => {
  return axios.put(`${FORMULARIOS_URL}`, formulario)
}

export const createCampo = (
  field: FormularioDtoCampoCreate,
  formId: ID
): Promise<FormularioCampoView> => {
  return axios.post(`${CAMPOS_URL}/${formId}`, field).then((resp) => resp.data)
}

export const updateCampo = (
  field: FormularioDtoCampoUpdate,
  id: ID
): Promise<FormularioCampoView> => {
  return axios.put(`${CAMPOS_URL}/${id}`, field).then((resp) => resp.data)
}

/**
 * Nova ordem dos campos numa chamada só (tudo ou nada), validada pelo backend sobre o estado
 * final. Substitui os N PUTs de campo, que geravam 400 falso em estado intermediário.
 * @param idFormulario ID do formulário
 * @param idsCampos lista COMPLETA dos ids dos campos, na nova ordem
 */
export const reordenarCampos = (idFormulario: ID, idsCampos: number[]): Promise<FormularioView> => {
  return axios
    .put(`${FORMULARIOS_URL}/${idFormulario}/campos/ordem`, { idsCampos })
    .then((resp: AxiosResponse<FormularioView>) => resp.data)
}

export const deleteCampos = (ids: number[]): Promise<any> => {
  return Promise.all(ids.map((id) => axios.delete(`${CAMPOS_URL}/${id}`)))
}

export const createOpcao = (
  option: FormularioDtoOpcaoCreate,
  fieldID: ID
): Promise<FormularioCampoView> => {
  return axios.post(`${OPCOES_URL}/${fieldID}`, option).then((resp) => resp.data)
}

export const updateOpcao = (
  option: FormularioDtoOpcaoUpdate,
  fieldID: ID
): Promise<FormularioCampoView> => {
  return axios.put(`${OPCOES_URL}/${fieldID}`, option).then((resp) => resp.data)
}

export const deleteOpcoes = (ids: ID[], fieldID: ID): Promise<unknown[]> => {
  return Promise.all(ids.map((id) => axios.delete(`${OPCOES_URL}/${fieldID}?idOpcao=${id}`)))
}

// AREA ADMIN - EMPRESA

/**
 * Detalhes de um formulário
 * @param formularioId ID do Formulário
 * @returns FormularioView
 */
export const getFormularioOfEmpresaById = (formularioId: number): Promise<FormularioView> => {
  return axios
    .get(`${FORMULARIOS_EMPRESA_URL}/${formularioId}`)
    .then((d: AxiosResponse<FormularioView>) => d.data)
}

/**
 * Cadastra formulário
 * @param empresaId ID Empresa
 * @param formulario FormularioDtoCreate
 * @returns FormularioView
 */
export const createFormularioOfEmpresa = (
  empresaId: number,
  formulario: FormularioDtoCreate
): Promise<FormularioView> => {
  return axios
    .post(`${FORMULARIOS_EMPRESA_URL}/${empresaId}`, formulario)
    .then((resp: AxiosResponse<FormularioView>) => resp.data)
}

/**
 *
 * @param empresaId ID Empresa
 * @param formulario FormularioDtoUpdate
 * @returns FormularioView
 */
export const updateFormularioOfEmpresa = (
  empresaId: number,
  formulario: FormularioDtoUpdate
): Promise<FormularioView> => {
  return axios
    .put(`${FORMULARIOS_EMPRESA_URL}/${empresaId}`, formulario)
    .then((resp: AxiosResponse<FormularioView>) => resp.data)
}

/**
 * Cadastrado campo de formulario na empresa
 * @param field FormularioDtoCampoCreate
 * @param formId ID Formulário
 * @returns FormularioCampoView
 */
export const createCampoOfEmpresa = (
  field: FormularioDtoCampoCreate,
  formId: ID
): Promise<FormularioCampoView> => {
  return axios.post(`${CAMPOS_EMPRESA_URL}/${formId}`, field).then((resp) => resp.data)
}

/**
 * Altera campo de um formulário da empresa
 * @param field FormularioCampoView
 * @param id ID do Campo
 * @returns FormularioCampoView
 */
export const updateCampoOfEmpresa = (
  field: FormularioDtoCampoUpdate,
  id: ID
): Promise<FormularioCampoView> => {
  return axios.put(`${CAMPOS_EMPRESA_URL}/${id}`, field).then((resp) => resp.data)
}

/**
 * Nova ordem dos campos de um formulário da empresa, numa chamada só (tudo ou nada).
 * @param idFormulario ID do formulário
 * @param idsCampos lista COMPLETA dos ids dos campos, na nova ordem
 */
export const reordenarCamposOfEmpresa = (
  idFormulario: ID,
  idsCampos: number[]
): Promise<FormularioView> => {
  return axios
    .put(`${FORMULARIOS_EMPRESA_URL}/${idFormulario}/campos/ordem`, { idsCampos })
    .then((resp: AxiosResponse<FormularioView>) => resp.data)
}

/**
 * Cria uma cópia editável de um formulário da empresa (usado quando o original está bloqueado).
 * @param idFormulario ID do formulário de origem
 * @returns FormularioView da cópia (com o id novo e a empresa)
 */
export const duplicarFormulario = (idFormulario: ID): Promise<FormularioView> => {
  return axios
    .post(`${FORMULARIOS_EMPRESA_URL}/duplicar/${idFormulario}`)
    .then((resp: AxiosResponse<FormularioView>) => resp.data)
}

/**
 * Deleta campos de um formulário
 * @param ids numbers[]
 */
export const deleteCamposOfEmpresa = (ids: number[]): Promise<any> => {
  return Promise.all(ids.map((id) => axios.delete(`${CAMPOS_EMPRESA_URL}/${id}`)))
}

/**
 * Cadastra opção de campo de um formulário de empresa
 * @param option FormularioDtoOpcaoCreate
 * @param fieldID ID do Campo
 */
export const createOpcaoOfEmpresa = (
  option: FormularioDtoOpcaoCreate,
  fieldID: ID
): Promise<FormularioCampoView> => {
  return axios.post(`${OPCOES_EMPRESA_URL}/${fieldID}`, option).then((resp) => resp.data)
}

/**
 * Altera opção de campo de um formulário de empresa
 * @param option FormularioDtoOpcaoUpdate
 * @param fieldID ID do Campo
 */
export const updateOpcaoOfEmpresa = (
  option: FormularioDtoOpcaoUpdate,
  fieldID: ID
): Promise<FormularioCampoView> => {
  return axios.put(`${OPCOES_EMPRESA_URL}/${fieldID}`, option).then((resp) => resp.data)
}

/**
 * Remove opçoes de campo de formulario
 * @param ids ID[]
 * @param fieldID ID do campo
 */
export const deleteOpcoesOfEmpresa = (ids: ID[], fieldID: ID): Promise<unknown[]> => {
  return Promise.all(
    ids.map((id) => axios.delete(`${OPCOES_EMPRESA_URL}/${fieldID}?idOpcao=${id}`))
  )
}
